import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaEdit, FaTrash, FaTimes, FaGripVertical, FaEye, FaClipboardList } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import AdminNavbar from "../Layout/AdminNavbar";
import {
  listTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from "../api/checklistTemplateApi";

/* ─── Inline Styles (matching Masters design system) ─── */
const S = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  .ct-wrap { font-family: 'DM Sans', sans-serif; background: #fff; min-height: 100vh; color: #1a1a1a; padding-top: 120px; }
  .ct-page { padding: 0 24px 40px; width: 100%; }

  .ct-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 22px; }
  .ct-header-left { display: flex; align-items: center; gap: 14px; }
  .ct-header-icon {
    width: 46px; height: 46px; border-radius: 12px; flex-shrink: 0;
    background: linear-gradient(135deg, #1e3a5f, #2d5586); color: #fff;
    display: flex; align-items: center; justify-content: center; font-size: 19px;
    box-shadow: 0 4px 10px rgba(30,58,95,0.25);
  }
  .ct-title { font-size: 26px; font-weight: 600; letter-spacing: -0.5px; color: #111; margin: 0; }
  .ct-subtitle { font-size: 13px; color: #888; margin: 4px 0 0; }
  .ct-actions { display: flex; gap: 10px; align-items: center; }

  .ct-stats-row {
    display: flex; align-items: center; gap: 22px; flex-wrap: wrap;
    background: #fafbfc; border: 1px solid #eef0ee; border-radius: 12px;
    padding: 14px 18px; margin-bottom: 18px;
  }
  .ct-stat { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #555; }
  .ct-stat strong { font-size: 16px; color: #111; font-weight: 700; }
  .ct-stat-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

  .btn-ghost-ct { background: #fff; border: 1px solid #e2e2e2; color: #444; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
  .btn-ghost-ct:hover { background: #f0f0ee; border-color: #ccc; }
  .btn-primary-ct { background: #1e3a5f; border: 1px solid #1e3a5f; color: #fff; padding: 8px 18px; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
  .btn-primary-ct:hover { background: #162d4a; }
  .btn-primary-ct:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-danger-ct { background: #c0392b; border: 1px solid #c0392b; color: #fff; padding: 8px 18px; border-radius: 8px; font-size: 13px; font-weight: 500; cursor: pointer; }
  .btn-danger-ct:hover { background: #a93226; }

  .ct-table-card { background: #fff; border: 1px solid #e8e8e6; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
  .ct-table-scroll {
    max-height: 58vh; overflow-y: auto;
    scrollbar-width: thin; scrollbar-color: #c9ccd3 transparent;
  }
  .ct-table-scroll::-webkit-scrollbar { width: 6px; }
  .ct-table-scroll::-webkit-scrollbar-track { background: transparent; }
  .ct-table-scroll::-webkit-scrollbar-thumb { background: #c9ccd3; border-radius: 10px; }
  .ct-table-scroll::-webkit-scrollbar-thumb:hover { background: #a8acb5; }
  .ct-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
  .ct-table th { text-align: left; padding: 13px 16px; font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #888; background: #fafbfc; border-bottom: 1px solid #eee; position: sticky; top: 0; z-index: 1; }
  .ct-table td { padding: 12px 16px; border-bottom: 1px solid #f2f2f0; vertical-align: middle; }
  .ct-table tbody tr { transition: background 0.12s ease; }
  .ct-table tbody tr:last-child td { border-bottom: none; }
  .ct-table tbody tr:hover { background: #fafbfc; }
  .ct-table tbody tr:hover .ct-icon-btn { color: #666; }

  .ct-name-cell { display: flex; align-items: center; gap: 12px; }
  .ct-avatar {
    width: 32px; height: 32px; border-radius: 9px; flex-shrink: 0; color: #fff;
    display: flex; align-items: center; justify-content: center; font-size: 12.5px; font-weight: 700;
  }

  .ct-empty { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 48px 20px; text-align: center; color: #999; }
  .ct-empty-icon {
    width: 46px; height: 46px; border-radius: 50%; background: #f4f5f7; color: #b7bcc7;
    display: flex; align-items: center; justify-content: center; font-size: 19px;
  }
  .ct-empty-text { font-size: 13.5px; }

  .ct-spinner {
    width: 16px; height: 16px; border: 2px solid #e5e7eb; border-top-color: #1e3a5f;
    border-radius: 50%; animation: ct-spin 0.7s linear infinite;
  }
  @keyframes ct-spin { to { transform: rotate(360deg); } }
  .ct-loading-row { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 40px; color: #888; font-size: 13.5px; }

  .ct-type-chip { display: inline-block; padding: 3px 10px; border-radius: 20px; font-size: 11.5px; font-weight: 600; }
  .ct-type-chip.general { background: #eef1f7; color: #1e3a5f; }
  .ct-type-chip.bug { background: #fdecea; color: #a93226; }
  .ct-type-chip.feature { background: #e9f7ef; color: #1e7145; }
  .ct-type-chip.improvement { background: #fef5e7; color: #9a6a12; }
  .ct-type-chip.task { background: #eef1f7; color: #34495e; }
  .ct-type-chip.parent { background: #f4ecf7; color: #6d3b7d; }

  .ct-icon-btn { background: none; border: none; color: #888; cursor: pointer; padding: 6px; border-radius: 6px; }
  .ct-icon-btn:hover { background: #f0f0ee; color: #333; }
  .ct-icon-btn.danger:hover { background: #fdecea; color: #c0392b; }

  .ct-name-link { cursor: pointer; font-weight: 500; }
  .ct-name-link:hover { color: #1e3a5f; text-decoration: underline; }

  .ct-view-meta { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
  .ct-view-meta-count { font-size: 12px; color: #999; }
  .ct-view-item-row { display: flex; align-items: center; gap: 10px; padding: 8px 10px; border-bottom: 1px solid #f2f2f0; font-size: 13.5px; color: #333; }
  .ct-view-item-row:last-child { border-bottom: none; }
  .ct-view-item-index { flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%; background: #f1f2f5; color: #888; font-size: 11px; font-weight: 600; display: flex; align-items: center; justify-content: center; }

  .ct-overlay { position: fixed; inset: 0; background: rgba(20,20,20,0.45); display: flex; align-items: center; justify-content: center; z-index: 2000; }
  .ct-modal {
    background: #fff; border-radius: 14px; width: 560px; max-width: 92vw; max-height: 88vh; overflow-y: auto;
    scrollbar-width: thin; scrollbar-color: #c9ccd3 transparent;
  }
  .ct-modal::-webkit-scrollbar { width: 6px; }
  .ct-modal::-webkit-scrollbar-track { background: transparent; }
  .ct-modal::-webkit-scrollbar-thumb { background: #c9ccd3; border-radius: 10px; }
  .ct-modal::-webkit-scrollbar-thumb:hover { background: #a8acb5; }
  .ct-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #eee; }
  .ct-modal-title { font-size: 17px; font-weight: 600; }
  .ct-modal-close { background: none; border: none; font-size: 20px; cursor: pointer; color: #888; }
  .ct-modal-body { padding: 20px 24px; }
  .ct-modal-footer { display: flex; justify-content: flex-end; gap: 10px; padding: 16px 24px; border-top: 1px solid #eee; }

  .ct-field { margin-bottom: 16px; }
  .ct-label { display: block; font-size: 12.5px; font-weight: 600; color: #555; margin-bottom: 6px; }
  .ct-input, .ct-select { width: 100%; padding: 9px 12px; border: 1px solid #ddd; border-radius: 8px; font-size: 13.5px; font-family: 'DM Sans', sans-serif; }
  .ct-checkbox-row { display: flex; align-items: center; gap: 8px; margin-bottom: 16px; }

  .ct-item-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; background: #fafafa; border: 1px solid #eee; border-radius: 8px; padding: 6px 10px; }
  .ct-item-row input { flex: 1; border: none; background: transparent; font-size: 13.5px; padding: 4px; }
  .ct-item-row input:focus { outline: none; }
  .ct-drag-handle { color: #bbb; cursor: grab; }
  .ct-items-list {
    max-height: 230px; overflow-y: auto; padding-right: 4px;
    scrollbar-width: thin; scrollbar-color: #c9ccd3 transparent;
  }
  .ct-items-list::-webkit-scrollbar { width: 6px; }
  .ct-items-list::-webkit-scrollbar-track { background: transparent; }
  .ct-items-list::-webkit-scrollbar-thumb { background: #c9ccd3; border-radius: 10px; }
  .ct-items-list::-webkit-scrollbar-thumb:hover { background: #a8acb5; }
  .ct-add-item-row { display: flex; gap: 8px; margin-bottom: 10px; }
`;

const TASK_TYPES = ["Task", "Feature", "Bug", "Improvement", "Parent"];

const SCOPE_COLORS = {
  general: "#1e3a5f",
  bug: "#a93226",
  feature: "#1e7145",
  improvement: "#9a6a12",
  task: "#34495e",
  parent: "#6d3b7d",
};
// A template with no specific taskType is effectively General, even if the
// isGeneral flag itself wasn't set correctly on the record — a task-specific
// template always has a taskType, so the absence of one means "applies to all".
const isEffectivelyGeneral = (t) => t.isGeneral || !t.taskType;
const scopeKey = (t) => (isEffectivelyGeneral(t) ? "general" : t.taskType.toLowerCase());
const scopeLabel = (t) => (isEffectivelyGeneral(t) ? "General" : t.taskType);
const scopeColor = (t) => SCOPE_COLORS[scopeKey(t)] || "#6b7280";

const blankForm = () => ({
  name: "",
  taskType: "Task",
  isGeneral: false,
  items: [], // [{ id?: number, text }]
});

function TemplateModal({ template, onClose, onSaved }) {
  const isEdit = !!template?.id;
  const [form, setForm] = useState(
    isEdit
      ? {
          name: template.name,
          taskType: template.taskType || "Task",
          isGeneral: !!template.isGeneral,
          items: (template.items || []).map((i) => ({ id: i.id, text: i.text })),
        }
      : blankForm()
  );
  const [newItemText, setNewItemText] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addItem = () => {
    const text = newItemText.trim();
    if (!text) return;
    set("items", [...form.items, { text }]);
    setNewItemText("");
  };

  const removeItem = (idx) => {
    set("items", form.items.filter((_, i) => i !== idx));
  };

  const updateItemText = (idx, text) => {
    set("items", form.items.map((it, i) => (i === idx ? { ...it, text } : it)));
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(form.items);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    set("items", items);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.warning("Template name is required.");
      return;
    }
    if (!form.isGeneral && !form.taskType) {
      toast.warning("Select a task type, or mark this template as General.");
      return;
    }
    if (form.items.length === 0) {
      toast.warning("Add at least one checklist item.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        taskType: form.isGeneral ? null : form.taskType,
        general: form.isGeneral,
        itemTexts: form.items.map((i) => i.text),
      };
      if (isEdit) {
        await updateTemplate(template.id, payload);
        toast.success("Template updated!");
      } else {
        await createTemplate(payload);
        toast.success("Template created!");
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || "Failed to save template.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ct-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ct-modal">
        <div className="ct-modal-header">
          <div className="ct-modal-title">{isEdit ? "Edit Checklist Template" : "New Checklist Template"}</div>
          <button className="ct-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="ct-modal-body">
          <div className="ct-field">
            <label className="ct-label">Template Name *</label>
            <input
              className="ct-input"
              placeholder="e.g. Bug QA Checklist"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </div>

          <div className="ct-checkbox-row">
            <input
              type="checkbox"
              id="ct-general"
              checked={form.isGeneral}
              onChange={(e) => set("isGeneral", e.target.checked)}
            />
            <label htmlFor="ct-general" style={{ fontSize: 13.5 }}>
              General — show for every task type
            </label>
          </div>

          {!form.isGeneral && (
            <div className="ct-field">
              <label className="ct-label">Task Type *</label>
              <select className="ct-select" value={form.taskType} onChange={(e) => set("taskType", e.target.value)}>
                {TASK_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="ct-field">
            <label className="ct-label">Checklist Items *</label>

            <div className="ct-add-item-row">
              <input
                className="ct-input"
                placeholder="Add a checklist point, press Enter…"
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addItem();
                  }
                }}
              />
              <button className="btn-ghost-ct" onClick={addItem}>
                <FaPlus size={11} /> Add
              </button>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="template-items">
                {(provided) => (
                  <div className="ct-items-list" ref={provided.innerRef} {...provided.droppableProps}>
                    {form.items.map((item, idx) => (
                      <Draggable key={idx} draggableId={`item-${idx}`} index={idx}>
                        {(dragProvided) => (
                          <div
                            className="ct-item-row"
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                          >
                            <span className="ct-drag-handle" {...dragProvided.dragHandleProps}>
                              <FaGripVertical size={12} />
                            </span>
                            <input value={item.text} onChange={(e) => updateItemText(idx, e.target.value)} />
                            <button className="ct-icon-btn danger" onClick={() => removeItem(idx)}>
                              <FaTimes size={12} />
                            </button>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>

        <div className="ct-modal-footer">
          <button className="btn-ghost-ct" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary-ct" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Template"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ViewTemplateModal({ template, onClose, onEdit }) {
  const items = template.items || [];
  return (
    <div className="ct-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ct-modal" style={{ width: 480 }}>
        <div className="ct-modal-header">
          <div className="ct-modal-title">{template.name}</div>
          <button className="ct-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="ct-modal-body">
          <div className="ct-view-meta">
            <span className={`ct-type-chip ${scopeKey(template)}`}>{scopeLabel(template)}</span>
            <span className="ct-view-meta-count">
              {items.length} item{items.length === 1 ? "" : "s"}
            </span>
          </div>

          <label className="ct-label">Checklist Items</label>
          {items.length === 0 ? (
            <div className="ct-empty" style={{ padding: 20 }}>
              This template has no items yet.
            </div>
          ) : (
            <div className="ct-items-list">
              {items.map((item, idx) => (
                <div key={item.id ?? idx} className="ct-view-item-row">
                  <span className="ct-view-item-index">{idx + 1}</span>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ct-modal-footer">
          <button className="btn-ghost-ct" onClick={onClose}>
            Close
          </button>
          <button className="btn-primary-ct" onClick={onEdit}>
            <FaEdit size={11} /> Edit Template
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ template, onClose, onConfirm }) {
  return (
    <div className="ct-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ct-modal" style={{ width: 420 }}>
        <div className="ct-modal-header">
          <div className="ct-modal-title">Deactivate Template</div>
          <button className="ct-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <div className="ct-modal-body">
          Deactivate <strong>{template.name}</strong>? It will no longer appear in the template dropdown on tasks.
          Tasks that already applied this template keep their checklist items.
        </div>
        <div className="ct-modal-footer">
          <button className="btn-ghost-ct" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger-ct" onClick={onConfirm}>
            Deactivate
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ChecklistTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [viewTemplate, setViewTemplate] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) {
      toast.error("You must be logged in.");
      navigate("/");
      return;
    }
    if (!roles.includes("ROLE_ADMIN")) {
      toast.error("Unauthorized.");
      navigate("/");
    }
  }, [navigate, token]);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTemplates(false);
      setTemplates(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load checklist templates.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDelete = async () => {
    try {
      await deleteTemplate(deleteCandidate.id);
      toast.success("Template deactivated.");
      setDeleteCandidate(null);
      fetchTemplates();
    } catch {
      toast.error("Failed to deactivate template.");
    }
  };

  const generalCount = templates.filter(isEffectivelyGeneral).length;
  const taskSpecificCount = templates.length - generalCount;
  const itemsCount = templates.reduce((sum, t) => sum + (t.items || []).length, 0);

  return (
    <>
      <style>{S}</style>
      <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}>
        <AdminNavbar />
      </div>

      <div className="ct-wrap">
        <div className="ct-page">
          <div className="ct-header">
            <div className="ct-header-left">
              <div className="ct-header-icon">
                <FaClipboardList />
              </div>
              <div>
                <h1 className="ct-title">Checklist Templates</h1>
                <p className="ct-subtitle">Manage reusable task checklists, scoped by task type</p>
              </div>
            </div>
            <div className="ct-actions">
              <button className="btn-ghost-ct" onClick={fetchTemplates}>
                ↻ Refresh
              </button>
              <button
                className="btn-primary-ct"
                onClick={() => {
                  setEditTemplate(null);
                  setShowForm(true);
                }}
              >
                <FaPlus size={11} /> New Template
              </button>
            </div>
          </div>

          {!loading && templates.length > 0 && (
            <div className="ct-stats-row">
              <div className="ct-stat">
                <span className="ct-stat-dot" style={{ background: "#1e3a5f" }} />
                <strong>{templates.length}</strong> Templates
              </div>
              <div className="ct-stat">
                <span className="ct-stat-dot" style={{ background: "#6d3b7d" }} />
                <strong>{generalCount}</strong> General
              </div>
              <div className="ct-stat">
                <span className="ct-stat-dot" style={{ background: "#34495e" }} />
                <strong>{taskSpecificCount}</strong> Task-Specific
              </div>
              <div className="ct-stat">
                <span className="ct-stat-dot" style={{ background: "#1e7145" }} />
                <strong>{itemsCount}</strong> Total Items
              </div>
            </div>
          )}

          <div className="ct-table-card">
            {loading ? (
              <div className="ct-loading-row">
                <span className="ct-spinner" />
                Loading templates…
              </div>
            ) : templates.length === 0 ? (
              <div className="ct-empty">
                <div className="ct-empty-icon">
                  <FaClipboardList />
                </div>
                <span className="ct-empty-text">No checklist templates yet. Create one to get started.</span>
              </div>
            ) : (
              <div className="ct-table-scroll">
              <table className="ct-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Scope</th>
                    <th>Items</th>
                    <th style={{ width: 130, textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <div className="ct-name-cell">
                          <div className="ct-avatar" style={{ background: scopeColor(t) }}>
                            {scopeLabel(t).slice(0, 2).toUpperCase()}
                          </div>
                          <span className="ct-name-link" onClick={() => setViewTemplate(t)}>
                            {t.name}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`ct-type-chip ${scopeKey(t)}`}>{scopeLabel(t)}</span>
                      </td>
                      <td>{(t.items || []).length}</td>
                      <td style={{ textAlign: "center" }}>
                        <button className="ct-icon-btn" title="View" onClick={() => setViewTemplate(t)}>
                          <FaEye size={13} />
                        </button>
                        <button
                          className="ct-icon-btn"
                          title="Edit"
                          onClick={() => {
                            setEditTemplate(t);
                            setShowForm(true);
                          }}
                        >
                          <FaEdit size={13} />
                        </button>
                        <button className="ct-icon-btn danger" title="Deactivate" onClick={() => setDeleteCandidate(t)}>
                          <FaTrash size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <TemplateModal
          template={editTemplate}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            fetchTemplates();
          }}
        />
      )}

      {deleteCandidate && (
        <ConfirmDeleteModal
          template={deleteCandidate}
          onClose={() => setDeleteCandidate(null)}
          onConfirm={handleDelete}
        />
      )}

      {viewTemplate && (
        <ViewTemplateModal
          template={viewTemplate}
          onClose={() => setViewTemplate(null)}
          onEdit={() => {
            setEditTemplate(viewTemplate);
            setViewTemplate(null);
            setShowForm(true);
          }}
        />
      )}
    </>
  );
}
